import * as Sentry from '@sentry/nextjs';
import { ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useSession } from 'next-auth/react';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import { ICartData, IConfiguratorCreativeProduct, ICreativeData, ICreativeExtraData, ICreativeImages } from '@features/cart/typescript';
import { useCartStore } from '@features/cart/mobx/context/CartStoreProvider';
import useRequestProgress from '@hooks/useRequestProgress';
import styles from '@features/productPreview/sections/Configurator/Configurator.styles';
import { getRegion, removeConfiguratorScripts } from '@utils/misc';
import { PUBLICATION_FLAG, SUPPORTED_OFFERS } from '@config/index';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import ReaffiliatesAddToCard from '@reaffiliates/ReaffiliatesAddToCart';
import { useOffer } from '@hooks/useOffer';
import NextProductApi from '@features/api/nextApiService/nextProductService';
import alertsStore from '@context/global/mobx/alertStore';
import popupStore from '@context/global/mobx/popupStore';
import { CONFIGURATOR_LOADING_TIMEOUT } from '@features/productPreview/const';
import CreativeModalContent from '@components/CreativeModalContent/CreativeModalContent';
import getPublicEnv from '@utils/getPublicEnv';
import logger from '@utils/logger';
import productCreativeAdapter from '@features/cart/adapters/configuratorCreative';
import ConfiguratorSkeleton from '@components/ConfiguratorSkeleton/ConfiguratorSkeleton';
import { IMetaData } from '@features/productPreview/typescript';
import { formatPrice } from '@utils/formatPrice';
import { TCreativeModalType } from '@components/CreativeModalContent/types';
import { AVAILABLE_REGIONS, DISPLAY_ECO_TAX_INFO } from '@const/index';
import { getSafeProductPagePath, sanitizeObjectUuid, sanitizePresetIndex } from '@utils/sanitizeProductQueryParams';
type PropsType = {
  productID: number;
  lang: string;
  metaData: IMetaData;
};
const Configurator = forwardRef(({
  productID,
  lang,
  metaData
}: PropsType, ref: ForwardedRef<HTMLDivElement>) => {
  const region = getRegion();
  const [valueUuId, setValueUuId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [valueModal, setValueModal] = useState(false);
  const [urlParams, setUrlParams] = useState(new URLSearchParams());
  const [projectCreated, setProjectCreated] = useState(false);
  const [creativeModalType, setCreativeModalType] = useState<TCreativeModalType>('saveProject');
  const {
    data: session
  } = useSession();
  const {
    t
  } = useTranslation('common');
  const {
    asPath,
    query
  } = useRouter();
  const {
    setCartCreated
  } = layoutUiState;
  const cartStore = useCartStore();
  const [configuratorLoading, setConfiguratorLoading] = useState(true);
  const [offer, priceList, storage] = useOffer();
  const [addProducts] = useRequestProgress(async (productArray: ICartData[]) => {
    setCartCreated(true);
    const addPositionsResponse = await cartStore.addProductsToCart(productArray);
    if (!addPositionsResponse.success) {
      alertsStore.pushAlert({
        message: t('alerts.header'),
        display: true
      });
      return;
    }
    await cartStore.fetchAndPopulateCartStore();
    cartStore.openMiniSideBarCart();
    ReaffiliatesAddToCard(productArray);
  });
  const handleScrollUp = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => false;
    }
    const loadingTimeout = setTimeout(() => {
      popupStore.pushPopup({
        message: t('alerts.configurator.loadingTimeout'),
        type: 'error',
        attachRefresh: true
      });
    }, CONFIGURATOR_LOADING_TIMEOUT);
    const urlParamsCurrent = new URLSearchParams(window.location.search);
    const objectUuidFromUrl = sanitizeObjectUuid(urlParamsCurrent.get('objectUuid'));
    const presetIndexFromUrl = sanitizePresetIndex(urlParamsCurrent.get('presetIndex'));
    const currentPath = window.location.pathname + (window.location.search ? `?${window.location.search}` : '');
    const safePath = getSafeProductPagePath(currentPath);
    if (safePath !== currentPath) {
      window.history.replaceState(null, '', safePath);
    }
    setUrlParams(new URLSearchParams(safePath.includes('?') ? safePath.slice(safePath.indexOf('?') + 1) : ''));
    window['creativeConfiguratorOpts'] = {
      container: document.getElementById('configurator'),
      offer,
      disableDeliveryInfo: getPublicEnv('NEXT_PUBLIC_CREATIVE_SHOW_DELIVERY_INFO') !== 'true',
      lang,
      preset: {
        id: productID
      },
      uuid: objectUuidFromUrl ?? '',
      presetIndex: presetIndexFromUrl ?? '0',
      orderCondCalcUrl: `${getPublicEnv('NEXT_PUBLIC_CONFIGURATOR_URI')}/api/dry-object/meta?metadata=creative.price_and_delivery&offer=${offer}&storages[]=${storage}&pricelists[]=${priceList}&lang=${lang}`,
      objectStorageUrl: `${getPublicEnv('NEXT_PUBLIC_CONFIGURATOR_URI')}/api/object?metadata=creative.images,creative.name,creative.iguana_upload&offer=${offer}&storages[]=${storage}&pricelists[]=${priceList}`,
      productCustomerUrl: objectUuidFromUrl ? `${getPublicEnv('NEXT_PUBLIC_CONFIGURATOR_URI')}/api/literal-object/${encodeURIComponent(objectUuidFromUrl)}` : ''
    };
    let creativeCartCallbacks = {};
    if (getPublicEnv('NEXT_PUBLIC_IS_SHOP') === 'true') {
      // NOTE: Creative configurator
      creativeCartCallbacks = {
        onSaveProject: region === AVAILABLE_REGIONS.euro ? undefined : async (_data: ICreativeData, _images: ICreativeImages, extraData: ICreativeExtraData) => {
          setCreativeModalType('saveProject');
          setValueUuId(extraData.objectUuid.uuid);
          setImageUrl(extraData.images.main.CART);
          handleScrollUp();
          setValueModal(true);
        },
        async onShareProject(_data: ICreativeData, _images: ICreativeImages, extraData: ICreativeExtraData) {
          setCreativeModalType('shareProject');
          setValueUuId(extraData.objectUuid.uuid);
          setImageUrl(extraData.images.main.CART);
          handleScrollUp();
          setValueModal(true);
        },
        async onAddToCart(data: ICreativeData, images: ICreativeImages, count: number, extraData: ICreativeExtraData) {
          const product: IConfiguratorCreativeProduct = {
            id: productID,
            data,
            images,
            count,
            extraData
          };
          const cartProducts: ICartData[] = productCreativeAdapter(product);
          await addProducts(cartProducts);
        },
        async onAddToVoxBox(_data: ICreativeData, _images: ICreativeImages, extraData: ICreativeExtraData) {
          const {
            uuid
          } = extraData.objectUuid;
          if (extraData.iguanaUploadData && extraData.iguanaUploadData.jsonUploaded) {
            const locale = `&locale=${lang}`;
            const windowUrl = `${getPublicEnv('NEXT_PUBLIC_VOXBOX_URL')}/StreamingAssets/inventory-item-redirect.html?identifier=${uuid}&version=${uuid}${locale}`;
            window.open(windowUrl, '_blank');
          } else {
            logger.error(`Error while adding to voxbox ${uuid}`);
            alertsStore.pushAlert({
              message: t('saveToVoxboxError'),
              display: true
            });
          }
        },
        async onSeeInShowroom() {
          const showroomSection = document.getElementById('showroom_section');
          if (showroomSection) {
            showroomSection.scrollIntoView({
              behavior: 'smooth'
            });
          }
        },
        async onAskQuestion() {
          if (offer === SUPPORTED_OFFERS.mx && typeof window !== 'undefined') {
            window.open('https://api.whatsapp.com/send/?phone=5215611547782&text&app_absent=0', '_blank');
          } else if (typeof window.LiveChatWidget !== 'undefined') {
            window.LiveChatWidget.call('maximize');
          }
        },
        ecoTaxInfo: DISPLAY_ECO_TAX_INFO === 'true'
      };
    }
    Object.assign(window['creativeConfiguratorOpts'], creativeCartCallbacks);
    const targetNode = document.getElementById('configurator');
    if (targetNode != null) {
      const config = {
        attributes: false,
        childList: true,
        subtree: true
      };
      const callback = (mutationsList: MutationRecord[], configuratorObserver: MutationObserver) => {
        const creativeConfigurator = mutationsList.some(mut => mut.target instanceof HTMLElement && (mut.target.classList.contains('creative-canvas-container') || mut.target.classList.contains('product-configurator--hidden')));
        if (creativeConfigurator) {
          configuratorObserver.disconnect();
          setTimeout(() => setConfiguratorLoading(false), 0);
          clearTimeout(loadingTimeout);
          const html: HTMLElement = document.getElementsByTagName('html')[0];
          html.style.overflowX = 'initial';
        }
      };
      const configuratorObserver = new MutationObserver(callback);
      configuratorObserver.observe(targetNode, config);
    } else {
      const canNotFindProductConfigurator: Sentry.Event = {
        message: 'Configurator not loaded on product page!',
        level: 'error',
        breadcrumbs: [{
          message: "Can not find '#configurator' element",
          level: 'error'
        }]
      };
      Sentry.captureEvent(canNotFindProductConfigurator);
    }
    NextProductApi.getConfiguratorBuild().then(result => {
      const script = document.createElement('script');
      const jsUrl = Object.keys(result).find(key => key.includes('app.js')) || '/build/app.js';
      script.setAttribute('src', `${getPublicEnv('NEXT_PUBLIC_CONFIGURATOR_URI') + result[jsUrl]}`);
      script.setAttribute('data-source', 'configuratorSource');
      document.head.appendChild(script);
    });
    return () => {
      const currentConfiguratorNode = document.getElementById('configurator');
      if (currentConfiguratorNode) currentConfiguratorNode.innerHTML = '';
      setConfiguratorLoading(true);
      removeConfiguratorScripts();
    };
  }, [asPath, query.objectUuid]);
  const handleSaveCreative = () => {
    const creativeAddSaveProjectButton = document.querySelectorAll('.price-container');
    const safeObjectUuid = urlParams ? sanitizeObjectUuid(urlParams.get('objectUuid')) : null;
    if (creativeAddSaveProjectButton && urlParams?.get('saveCreative') && safeObjectUuid) {
      setValueUuId(safeObjectUuid);
      setImageUrl(urlParams.get('imageUrl') ?? '');
      setValueModal(true);
    }
  };
  const handleOverflowHtml = () => {
    const htmlObj = document.querySelector('html');
    if (htmlObj) {
      htmlObj.style.overflowY = 'unset';
    }
  };
  useEffect(() => {
    handleSaveCreative();
    if (!configuratorLoading) {
      handleOverflowHtml();
    }
  }, [configuratorLoading, handleSaveCreative]);
  useEffect(() => {
    if (!valueModal) {
      setProjectCreated(false);
    }
  }, [valueModal]);
  const floatPrice = parseFloat(metaData.price);
  let price = '...';
  if (!Number.isNaN(floatPrice)) {
    price = formatPrice(floatPrice * 100);
  }
  return <>
      <CreativeModalContent modalType={creativeModalType} isUserLogged={session} active={valueModal} isProjectCreated={projectCreated} setProjectCreated={setProjectCreated} uuId={valueUuId} imageUrl={imageUrl} setValueModal={setValueModal} />
      <ErrorBoundary>
        {configuratorLoading && <ConfiguratorSkeleton productName={metaData.name} productImage={metaData.image} productPrice={price} />}
        <div sx={styles.configuratorContainer(configuratorLoading)}>
          <div ref={ref} id="configurator" product-id={productID} lang={lang} publication={PUBLICATION_FLAG} offer={offer} pricelists={priceList} storages={storage} sx={styles.configuratorContent} />
        </div>
      </ErrorBoundary>
    </>;
});
Configurator.displayName = 'Configurator';
export default observer(Configurator);